(ns app.captions.transcript-edit
  "Keeps the timestamps of the transcript words while the transcript is
   edited as plain text (see `ui.captions.editor`).

   The editor hands over its text as tokens (the blank-separated
   pieces), each with the words it was made of and whether a phrase
   break stands before it:

     {:text \"Storrito\" :words [{:word/uuid ... :word/start 120 :word/end 480}] :break? false}

   `reconcile` turns them into the new words:

   - a token of one word is that word with the token's text, so a typo
     fix keeps the timing
   - a token of several words is their merge (the blank between them was
     removed): from the first start to the last end
   - the tokens that share one word are its split (a blank was typed
     into it): they divide its time in proportion to their lengths
   - a token's phrase break (`:break?`, Enter in the editor) is the
     word's `:word/break?`: the caption styles start a new phrase there
   - a token without a word is new (typed or pasted): it gets the pause
     between its neighbours, or a part of a neighbour's time when there
     is no pause. The editor keeps such a word `:word/fresh?`, and a
     fresh word is timed again with every change, since its time was a
     guess for the first typed character

   Pure and shared with the JVM, so the rules are covered by
   `app.captions.transcript-edit-test`. The split and merge rules come
   from the transcript editor of Audiocado (`app.transcript-editor.edit`)."
  (:require [clojure.string :as str]))

(def min-word-ms
  "A new word is never squeezed into a shorter pause than this, it
   takes a part of its neighbour's time instead."
  100)

(defn natural-ms
  "A guess of how long a word is spoken: about 80 ms per character."
  [text]
  (-> (* 80 (count text))
      (max 160)
      (min 800)))

(defn round
  [x]
  (long (Math/round (double x))))

(defn merged
  "One word out of the words a token is made of."
  [words]
  (cond-> (assoc (first words)
                 :word/start (apply min (map :word/start words))
                 :word/end (apply max (map :word/end words)))
    (some :word/emphasis? words) (assoc :word/emphasis? true)))

(defn claim-token
  "Decides what a token is: a word that is `::kept` (the first token
   of the words it is made of), a `::split` piece of the word of the
   previous token, or `::new` (without a word, or of fresh words, which
   keep their uuid)."
  [{:keys [claimed previous] :as acc} {:keys [text words break?]}]
  (let [unclaimed (remove (comp claimed :word/uuid) words)
        entry (cond
                (and (seq unclaimed)
                     (every? :word/fresh? unclaimed))
                (assoc (select-keys (merged unclaimed)
                                    [:word/uuid :word/emphasis?])
                       ::kind ::new)

                (seq unclaimed)
                (assoc (dissoc (merged unclaimed) :word/fresh?)
                       ::kind ::kept)

                (and previous
                     (= previous (:word/uuid (first words))))
                {::kind ::split
                 ::of previous}

                :else
                {::kind ::new})]
    (-> acc
        (update :claimed into (map :word/uuid unclaimed))
        (assoc :previous (or (:word/uuid entry)
                             (::of entry)))
        (update :entries conj (cond-> (assoc entry
                                             :word/text text
                                             :word/break? (boolean break?))
                                (and (not (seq unclaimed))
                                     (some :word/emphasis? words))
                                (assoc :word/emphasis? true))))))

(defn claim
  [tokens]
  (:entries
    (reduce claim-token
            {:claimed #{}
             :previous nil
             :entries []}
            tokens)))

(defn distribute
  "Divides `[start end]` among the texts in proportion to their
   lengths: a vector of `[start end]`, gapless."
  [start end texts]
  (let [span (- end start)
        total (max 1 (reduce + (map count texts)))
        bounds (->> texts
                    (reductions (fn [sum text]
                                  (+ sum (count text)))
                                0)
                    (mapv (fn [sum]
                            (+ start (round (/ (* span sum) total))))))]
    (mapv vector bounds (rest bounds))))

(defn with-times
  [entries times]
  (mapv (fn [entry [start end]]
          (assoc entry
                 :word/start start
                 :word/end end))
        entries
        times))

(defn split-group
  "A kept word and the split pieces that follow it share its time. The
   pieces of a fresh word have no time to share, they are new words."
  [[word & pieces :as group]]
  (cond
    (empty? pieces)
    group

    (:word/start word)
    (with-times group
                (distribute (:word/start word)
                            (:word/end word)
                            (map :word/text group)))

    :else
    (mapv (fn [entry]
            (assoc entry ::kind ::new))
          group)))

(defn split
  [entries]
  (->> entries
       (reduce (fn [groups entry]
                 (if (and (= ::split (::kind entry))
                          (seq groups))
                   (update groups (dec (count groups)) conj entry)
                   (conj groups [entry])))
               [])
       (into [] (mapcat split-group))))

(defn timed?
  [entry]
  (some? (:word/start entry)))

(defn untimed-runs
  "The index ranges `[from to]` (`to` exclusive) of the consecutive
   entries without a time."
  [entries]
  (->> (map-indexed vector entries)
       (partition-by (comp timed? second))
       (remove (comp timed? second first))
       (mapv (fn [run]
               [(first (first run))
                (inc (first (last run)))]))))

(defn sequential
  "Natural durations one after the other from `start`."
  [start texts]
  (let [bounds (reductions + start (map natural-ms texts))]
    (mapv vector bounds (rest bounds))))

(defn fill-run
  "The times of a run of new words between the timed words `before`
   and `after` (each may be missing at the ends of the `clip-range`
   `{:lo :hi}`). The words take the pause between the two
   with their natural durations, share it when it is too short for
   that, and share the time of `before` (else `after`) when there is
   no pause worth a word."
  [entries {:keys [lo hi]} [from to]]
  (let [before (when (pos? from)
                 (nth entries (dec from)))
        after (when (< to (count entries))
                (nth entries to))
        run (subvec entries from to)
        texts (map :word/text run)
        needed (reduce + (map natural-ms texts))
        lo (or (:word/end before) lo 0)
        hi (or (:word/start after) hi (+ lo needed))
        room (- hi lo)
        put (fn [entries index times]
              (reduce (fn [entries [offset time]]
                        (update entries
                                (+ index offset)
                                (fn [entry]
                                  (first (with-times [entry] [time])))))
                      entries
                      (map-indexed vector times)))]
    (cond
      (<= needed room)
      (put entries
           from
           (sequential (if (or before (nil? after))
                         lo
                         (- hi needed))
                       texts))

      (<= (* min-word-ms (count run)) room)
      (put entries from (distribute lo hi texts))

      before
      (put entries
           (dec from)
           (distribute (:word/start before)
                       (max hi (:word/end before))
                       (cons (:word/text before) texts)))

      after
      (put entries
           from
           (distribute (min lo (:word/start after))
                       (:word/end after)
                       (concat texts [(:word/text after)])))

      :else
      (put entries from (sequential lo texts)))))

(defn fill
  [entries clip-range]
  (reduce (fn [entries run]
            (fill-run entries clip-range run))
          entries
          (untimed-runs entries)))

(defn word
  "The entry as word map. A word lasts at least a millisecond, since a
   word without a length is dropped by the caption compositions."
  [new-uuid entry]
  (let [start (:word/start entry)]
    (cond-> {:word/uuid (or (:word/uuid entry)
                            (new-uuid))
             :word/text (:word/text entry)
             :word/start start
             :word/end (max (:word/end entry)
                            (inc start))
             :word/emphasis? (boolean (:word/emphasis? entry))
             :word/break? (boolean (:word/break? entry))}
      (= ::new (::kind entry)) (assoc :word/fresh? true))))

(defn reconcile
  "The words of the edited text, in its order. `clip-range` is the part of
   the media that the captions clip shows (`{:lo :hi}`, ms), where new
   words are placed when they have no neighbours. `new-uuid` makes the
   `:word/uuid` of a new word."
  ([tokens clip-range]
   (reconcile tokens clip-range random-uuid))
  ([tokens clip-range new-uuid]
   (let [entries (->> tokens
                      (remove (comp str/blank? :text))
                      (claim)
                      (split))]
     (mapv (partial word new-uuid)
           (fill entries clip-range)))))

(def edited-attributes
  [:word/text :word/start :word/end :word/emphasis? :word/break?])

(defn comparable
  [word]
  (-> (select-keys word edited-attributes)
      (update :word/emphasis? boolean)
      (update :word/break? boolean)))

(defn changes
  "What separates the `stored` words from the `edited` ones: the uuids
   to `:retract`, the words to `:add` and the ones to `:update`."
  [stored edited]
  (let [stored-by-uuid (into {} (map (juxt :word/uuid identity)) stored)
        edited-uuids (into #{} (map :word/uuid) edited)]
    {:retract (into []
                    (comp (map :word/uuid)
                          (remove edited-uuids))
                    stored)
     :add (into []
                (remove (comp stored-by-uuid :word/uuid))
                edited)
     :update (into []
                   (filter (fn [word]
                             (when-let [old (stored-by-uuid (:word/uuid word))]
                               (not= (comparable old)
                                     (comparable word)))))
                   edited)}))

(defn with-indexes
  "A new word gets the `:word/index` after the one of the word before
   it (the words are ordered by start, then index)."
  [stored edited]
  (let [index-of (into {} (map (juxt :word/uuid :word/index)) stored)]
    (:words
      (reduce (fn [{:keys [index] :as acc} word]
                (let [index (or (index-of (:word/uuid word))
                                (inc index))]
                  (-> acc
                      (assoc :index index)
                      (update :words conj (assoc word :word/index index)))))
              {:index -1
               :words []}
              edited))))

(defn stored-word
  "The attributes of a word as the transaction writes them. The flags
   are coerced: a `false` that travelled through the action dispatch
   arrives as nil (`parts.replicant.core/enrich-action` keeps the first
   truthy value of its enrichers), and DataScript refuses nil."
  [word attributes]
  (-> (select-keys word attributes)
      (update :word/emphasis? boolean)
      (update :word/break? boolean)))

(defn tx
  "The DataScript transaction that turns the `stored` words of the
   media asset into the `edited` ones. Only what the editor edits is
   written, so the other attributes of a word (emoji, color) stay, and
   the editor's `:word/fresh?` stays out of the design."
  [media-asset-uuid stored edited]
  (let [{retracted :retract
         added :add
         updated :update} (changes stored
                                   (with-indexes stored edited))]
    (vec
      (concat
        (map (fn [word-uuid]
               [:db/retractEntity [:word/uuid word-uuid]])
             retracted)
        (map (fn [word]
               (stored-word word (cons :word/uuid edited-attributes)))
             updated)
        (when (seq added)
          [{:asset/uuid media-asset-uuid
            :asset/words (mapv (fn [word]
                                 (stored-word word
                                              (list* :word/uuid
                                                     :word/index
                                                     edited-attributes)))
                               added)}])))))

(comment
  (reconcile [{:text "Hello"
               :words [{:word/uuid 1 :word/start 0 :word/end 400}]}
              {:text "big"
               :words []}
              {:text "world"
               :words [{:word/uuid 2 :word/start 1000 :word/end 1500}]}]
             {:lo 0 :hi 5000})
  )
